import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "../../../components/ui";
import { ROUTES } from "../../../routes/paths";
import { errorText } from "../../../lib/http";
import AuthLayout from "../components/AuthLayout";
import { AUTH_ERROR_CLASSES, authFieldClasses } from "../components/authFields";
import { login } from "../services/authService";

/**
 * Step one of signing in: the address and the password.
 *
 * Accepting them does not sign anybody in. It asks the server to mail a
 * one-time code and moves to the screen that takes it — the session is created
 * there and nowhere else, once both factors have been accepted. So a stolen
 * password on its own opens nothing, which is the whole point of the second
 * screen existing.
 *
 * Drawn as the dietitian app's log-in screen is drawn: one column on white, no
 * card, placeholders instead of labels.
 */
const LoginPage: React.FC = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [apiError, setApiError] = useState("");

  const validateEmail = (value: string): string => {
    if (!value) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const validatePassword = (value: string): string => {
    if (!value) return "Password is required";
    if (value.length < 6) return "Password must be at least 6 characters";
    return "";
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmail(value);
    setApiError("");
    if (emailTouched) setEmailError(validateEmail(value));
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPassword(value);
    setApiError("");
    if (passwordTouched) setPasswordError(validatePassword(value));
  };

  /**
   * An empty field that was never typed in is not wrong yet, it is just
   * unused — flagging it on the way past would greet somebody who tabbed
   * through the form with two errors about a form they have not filled in.
   * Submitting still asks for both.
   */
  const handleBlur = (
    value: string,
    validate: (value: string) => string,
    setTouched: (touched: boolean) => void,
    setError: (message: string) => void
  ) => {
    if (!value) {
      setError("");
      return;
    }
    setTouched(true);
    setError(validate(value));
  };

  const handleSubmit = async () => {
    const emailErr = validateEmail(email);
    const passwordErr = validatePassword(password);

    setEmailError(emailErr);
    setPasswordError(passwordErr);
    setEmailTouched(true);
    setPasswordTouched(true);
    setApiError("");

    if (emailErr || passwordErr) return;

    setSubmitting(true);
    try {
      await login(email, password);
      // Both travel in router state rather than in the URL: an address (or a
      // password) in a path is an address in browser history and in every
      // screenshot of the bar. The password rides along too, not just the
      // address — it is what "Resend" on the next screen asks the login
      // endpoint with again, since that is the one thing on the server that
      // actually sends the code.
      navigate(ROUTES.verifyOtp, { state: { email, password } });
    } catch (error) {
      setApiError(
        errorText(
          error,
          "We could not reach the server. Check your connection and try again."
        )
      );
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <h1 className="text-[26px] 3xl:text-[28px] leading-tight font-medium text-ink">
        Admin panel login
      </h1>

      <form
        className="mt-7 space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <div>
          <input
            type="email"
            value={email}
            onChange={handleEmailChange}
            onBlur={() =>
              handleBlur(
                email,
                validateEmail,
                setEmailTouched,
                setEmailError
              )
            }
            disabled={submitting}
            autoComplete="username"
            autoFocus
            aria-label="Registered email"
            className={authFieldClasses(!!emailError && emailTouched)}
            placeholder="yourname@email.com"
          />
          {emailError && emailTouched && (
            <p className={AUTH_ERROR_CLASSES}>{emailError}</p>
          )}
        </div>

        <div>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={handlePasswordChange}
              onBlur={() =>
                handleBlur(
                  password,
                  validatePassword,
                  setPasswordTouched,
                  setPasswordError
                )
              }
              disabled={submitting}
              autoComplete="current-password"
              aria-label="Password"
              className={`${authFieldClasses(
                !!passwordError && passwordTouched
              )} pr-12`}
              placeholder="Enter the password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded text-[#9AA4AE] hover:text-ink transition-colors"
            >
              {showPassword ? (
                <Eye className="w-5 h-5" />
              ) : (
                <EyeOff className="w-5 h-5" />
              )}
            </button>
          </div>
          {passwordError && passwordTouched && (
            <p className={AUTH_ERROR_CLASSES}>{passwordError}</p>
          )}
        </div>

        {apiError && (
          <p role="alert" className="text-xs text-[#D14343]">
            {apiError}
          </p>
        )}

        {/* Live even with the fields empty. A submit that is disabled until a
            form validates says nothing about why — pressing this one answers
            the question instead, by naming what is missing. */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={submitting}
        >
          Log in
        </Button>
      </form>
    </AuthLayout>
  );
};

export default LoginPage;
